import { CheckIcon } from 'lucide-react';
import { Link } from 'react-router';

import routes from '@/assets/constants/routes-no.ts';
import { ComparisonTable } from '@/components/marketing/comparison-table/index.ts';
import { EconomySection } from '@/components/marketing/economy-section/economy-section.tsx';
import { FaqSection } from '@/components/marketing/faq-section/index.ts';
import { Section } from '@/components/marketing/section/index.ts';
import { TestimonialsSection } from '@/components/marketing/testimonials-section/index.ts';
import { TestimonialsRating } from '@/components/testimonials/index.ts';
import { testimonialsQuery } from '@/lib/api/requests.ts';
import { getQueryClient } from '@/lib/query-client-context.ts';
import { SEO } from '@/lib/seo.tsx';

import type { Route } from './+types/consumer-loan.ts';
import Arrow from './-internal/arrow.svg?react';
import { Calculator } from './-internal/calculator/calculator.tsx';
import Drops from './-internal/drops.svg?react';
import ogImage from './-internal/og-image.png';
import step1 from './-internal/step-1.svg';
import step2 from './-internal/step-2.svg';
import step3 from './-internal/step-3.svg';

export async function loader({ context }: Route.LoaderArgs) {
  const queryClient = getQueryClient(context);
  const testimonials = await queryClient.query(testimonialsQuery());

  return { testimonials };
}

export default function ConsumerLoan({ loaderData }: Route.ComponentProps) {
  return (
    <div className="flex w-full flex-col bg-white text-v1-background-200">
      <SEO
        title="Søk forbrukslån hos flere banker"
        description="Sammenlign forbrukslån fra flere banker hos Scoopr. Gratis og uforpliktende."
        path={routes.CONSUMER_LOAN}
        image={{ src: ogImage, alt: 'Forbrukslån hos Scoopr' }}
      />

      <article className="flex flex-col gap-8 sm:gap-15 lg:mt-15">
        <Section.Container>
          <Section.Content className="flex flex-col items-center gap-8 py-10 lg:flex-row lg:items-start lg:gap-16">
            <div className="flex-1">
              <h1 className="mb-4 text-center typography-v1-h1 text-balance max-lg:mx-auto lg:mb-8 lg:min-w-lg lg:text-left lg:typography-v1-display">
                <span className="relative">
                  <Drops className="absolute top-0 -left-4 lg:top-4 lg:scale-200" />
                  Søk forbrukslån hos flere banker
                  <Arrow className="absolute -right-1 bottom-0 translate-full max-sm:hidden lg:-scale-x-150 lg:scale-y-150 lg:-rotate-90" />
                </span>
              </h1>

              <div className="mx-auto block max-w-sm text-center typography-v1-body4 text-balance lg:hidden">
                La bankene kjempe om deg som kunde! <strong>Send èn søknad</strong>, få svar fra
                inntil <strong>10 banker</strong> og velg det lånet som{' '}
                <strong>passer deg best</strong>.
              </div>

              <div className="hidden lg:block">
                <div className="typography-v1-body2">
                  <p className="mb-8">
                    Ønsker du forbrukslån med lavest mulig rente?
                    <br />
                    La bankene konkurrere om deg!
                  </p>
                  <ul className="mb-8 flex flex-col gap-4">
                    <li className="flex items-center gap-2">
                      <CheckIcon className="text-v1-primary-100" />
                      Send <strong>én søknad</strong>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckIcon className="text-v1-primary-100" />
                      Få svar fra inntil <strong>13 banker</strong>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckIcon className="text-v1-primary-100" />
                      Velg det lånet som <strong>passer deg best</strong>
                    </li>
                  </ul>
                </div>
                <TestimonialsRating data={loaderData.testimonials} />
              </div>
            </div>

            <div className="max-w-lg">
              <Calculator />
            </div>
          </Section.Content>
        </Section.Container>

        <HowItWorksSection />
        <WhatIsAConsumerLoanSection />
        <TestimonialsSection data={loaderData.testimonials} />
        <LoanRulesSection />
        <FaqSection items={faqItems} />
        <EconomySection />
      </article>
    </div>
  );
}

const faqLinkClassName = 'font-bold text-v1-primary-100 underline';

const faqItems = [
  {
    title: 'Hvor lang nedbetalingstid kan jeg få på et forbrukslån?',
    content: (
      <p>
        Maksimalt fem år. Utlånsforskriften krever at nye forbrukslån nedbetales innenfor fem år.
        Lengre løpetid er bare mulig ved{' '}
        <Link to={routes.PRODUCTS_REFINANCING} className={faqLinkClassName}>
          refinansiering
        </Link>
        , der du kan videreføre den lengste løpetiden fra de lånene du innfrir.
      </p>
    ),
  },
  {
    title: 'Hvor mye kan jeg låne?',
    content: (
      <p>
        Hos Scoopr kan du søke om <strong>10 000 kr</strong> til <strong>600 000 kr</strong>. Hvor
        mye du faktisk får avhenger av inntekten din og hvor mye gjeld du har fra før. Samlet gjeld
        skal normalt ikke overstige fem ganger bruttoinntekten din.
      </p>
    ),
  },
  {
    title: 'Påvirker det kredittscoren min å søke?',
    content: (
      <p>
        Banken gjør en{' '}
        <Link to={routes.CREDIT_CHECK_MYSELF} className={faqLinkClassName}>
          kredittsjekk
        </Link>{' '}
        når du søker, og du får varsel om den. Mange søknader på kort tid kan påvirke vurderingen
        negativt. Det er en av grunnene til at én søknad til flere banker er bedre enn å søke hos
        hver bank for seg.
      </p>
    ),
  },
  {
    title: 'Kan jeg få forbrukslån med betalingsanmerkning?',
    content: (
      <p>
        Det er vanskelig å få lån uten sikkerhet med en aktiv betalingsanmerkning. Noen banker
        vurderer søknaden hvis du kan stille sikkerhet eller søke med medsøker.
      </p>
    ),
  },
  {
    title: 'Hva koster tjenesten?',
    content: (
      <p>
        Ingenting. Scoopr er en låneformidler med konsesjon fra Finanstilsynet, og vi får betalt av
        banken hvis du takker ja til et tilbud. Det påvirker ikke renten du får.
      </p>
    ),
  },
  {
    title: 'Hva ser Scoopr når jeg deler gjeldsopplysningene mine?',
    content: (
      <p>
        Vi ser den usikrede gjelden din: forbrukslån,{' '}
        <Link to={routes.CREDIT_CARDS} className={faqLinkClassName}>
          kredittkort
        </Link>
        , rammekreditter og faktureringskort. Vi ser ikke boliglån, billån eller andre lån med
        sikkerhet. Du velger selv om du vil dele.
      </p>
    ),
  },
];

const HowItWorksItem = (props: {
  step: string;
  title: string;
  description: string;
  illustration: string;
}) => {
  return (
    <li className="col-span-2 flex flex-col items-center gap-8 rounded-2xl border border-v1-background-80 bg-v1-background-60 p-8">
      <img
        className="size-37.5 overflow-hidden rounded-full bg-v1-background-80"
        src={props.illustration}
        alt=""
        loading="lazy"
        decoding="async"
      />
      <div className="flex w-full flex-col gap-1 text-center">
        <p className="typography-v1-h4 text-v1-primary-100">{props.step}</p>
        <div className="flex flex-col items-center gap-3">
          <h3 className="w-full typography-v1-h4">{props.title}</h3>
          <p className="w-full typography-v1-body4 text-balance">{props.description}</p>
        </div>
      </div>
    </li>
  );
};

const HowItWorksSection = () => {
  return (
    <Section.Container>
      <Section.Content className="flex flex-col items-center text-center">
        <h2 className="mb-8 typography-v1-h1">Hvordan fungerer det?</h2>

        {/* Columns are doubled and each card spans two, so a card left alone on the
            last row can start half a card in and stay centered at full card width. */}
        <ul className="grid w-full grid-cols-2 gap-6 sm:grid-cols-4 lg:grid-cols-6 lg:gap-8 sm:max-lg:[&>li:last-child:nth-child(odd)]:col-start-2">
          <HowItWorksItem
            step="01"
            title="Fullfør lånesøknaden"
            description="Fullfør søknaden, så sender vi den til alle våre partnerbanker."
            illustration={step1}
          />
          <HowItWorksItem
            step="02"
            title="Sammenlign tilbud"
            description="Bankene kommer med tilbudene, og du velger selv hvilket tilbud du ønsker å gå videre med."
            illustration={step2}
          />
          <HowItWorksItem
            step="03"
            title="Få pengene utbetalt"
            description="Signer kontrakten og få pengene utbetalt av banken."
            illustration={step3}
          />
        </ul>
      </Section.Content>
    </Section.Container>
  );
};

const WhatIsAConsumerLoanSection = () => {
  return (
    <Section.Container>
      <Section.Content
        size="sm"
        className="flex flex-col gap-8 py-10 typography-v1-body4 md:flex-row"
      >
        <div className="flex flex-1 flex-col gap-4">
          <h2 className="typography-v1-h3">Hva er et forbrukslån?</h2>
          <p>
            Et forbrukslån er et lån uten sikkerhet. Banken tar ikke pant i bolig, bil eller andre
            eiendeler, og vurderer i stedet betalingsevnen din. Fordi banken ikke har sikkerhet å gå
            på, er renten høyere enn på boliglån.
          </p>
          <p>
            Du kan bruke pengene til hva du vil. Det er også den viktigste forskjellen fra
            refinansiering, der pengene må gå til å innfri gjeld du allerede har.
          </p>
        </div>
        <div className="flex flex-1 flex-col gap-4">
          <h2 className="typography-v1-h3">Renten du får er din egen</h2>
          <p>
            Renten på et forbrukslån settes individuelt. To personer som søker samme beløp kan få
            flere prosentpoeng forskjell, avhengig av inntekt, gjeld og betalingshistorikk. Rentene
            du ser i markedsføring er intervaller, ikke tilbud.
          </p>
          <p>
            Sammenlign alltid på effektiv rente, ikke nominell. Den effektive renten inkluderer
            etableringsgebyr og termingebyrer.
          </p>
        </div>
      </Section.Content>
    </Section.Container>
  );
};

const LoanRulesSection = () => {
  return (
    <Section.Container>
      <Section.Content size="sm" className="flex flex-col gap-8 py-10 typography-v1-body4">
        <div className="flex flex-col gap-4">
          <h2 className="typography-v1-h3">
            Nytt lån eller refinansiering? Reglene er ikke de samme
          </h2>
          <p>Dette er verdt å forstå før du søker, fordi det avgjør hva du faktisk kan få.</p>
          <p>
            Utlånsforskriften stiller strengere krav til nye forbrukslån enn til refinansiering:
          </p>

          <ComparisonTable
            columns={['Vilkår', 'Nytt forbrukslån', 'Refinansiering']}
            rows={[
              [
                'Nedbetalingstid',
                'Maks 5 år',
                'Kan videreføre lengste løpetid fra de opprinnelige lånene',
              ],
              ['Krav til betjeningsevne', 'Ja', 'Unntatt på gitte vilkår'],
              [
                'Krav til gjeldsgrad',
                'Ja, samlet gjeld normalt maks 5 ganger bruttoinntekt',
                'Unntatt på gitte vilkår',
              ],
              ['Hva pengene kan brukes til', 'Fritt', 'Må innfri eksisterende gjeld'],
            ]}
          />

          <p>
            Konsekvensen er praktisk: et nytt forbrukslån på 200 000 kroner må nedbetales over fem
            år, mens de samme 200 000 kronene i refinansiering kan fordeles over lengre tid og gi
            lavere månedsbeløp. Har du allerede dyr gjeld, er det ofte refinansiering som gir mest
            igjen, ikke et nytt lån.
          </p>
        </div>

        <div className="flex flex-col gap-4">
          <h2 className="typography-v1-h3">Slik vurderer banken søknaden din</h2>
          <p>Bankene i nettverket vårt ser på fire ting:</p>
          <ol className="flex list-decimal flex-col gap-4 pl-6 marker:font-bold">
            <li>
              <strong>Inntekt.</strong> Fast, dokumenterbar inntekt. De fleste bankene har en nedre
              grense.
            </li>
            <li>
              <strong>Samlet gjeld.</strong> Banken er pålagt å hente inn gjeldsopplysningene dine
              fra et gjeldsinformasjonsforetak. All usikret gjeld er synlig, også kredittkortrammer
              du ikke bruker.
            </li>
            <li>
              <strong>Betjeningsevne.</strong> Banken regner på om du tåler en renteøkning på lånet
              og fortsatt har nok til å leve av.
            </li>
            <li>
              <strong>Betalingshistorikk.</strong> Betalingsanmerkninger gjør det vanskelig å få lån
              uten sikkerhet.
            </li>
          </ol>
          <p>
            Bankene har en fleksibilitetskvote som lar dem innvilge et lite antall lån utenfor
            kravene til betjeningsevne og gjeldsgrad. Kvoten er på fem prosent av innvilget volum
            per kvartal, og bankene bruker den langt fra fullt ut. Den er ikke noe du kan søke om,
            men det betyr at et avslag hos én bank ikke alltid betyr avslag hos alle.
          </p>
        </div>
      </Section.Content>
    </Section.Container>
  );
};
